import { create } from "zustand";
import { authService } from "./authService";
import { AuthState, User } from "./types";
import { TOKEN_KEY, USER_KEY } from "../../utils/const";

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  isLoading: false,
  error: null,
  verifyPhoneResult: null,
  verifyOtpResult: null,
  phoneNotRegistered: false,

  customerRegister: async (input) => {
    set({ isLoading: true, error: null });

    try {
      const data = await authService.customerRegister(input);
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      set({ user: data.user, token: data.token, isLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Register failed";
      set({
        isLoading: false,
        error: message,
      });
      throw new Error(message);
    }
  },

  login: async (input) => {
    set({ isLoading: true, error: null });

    try {
      const data = await authService.login(input);
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      set({ user: data.user, token: data.token, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Login failed";
      set({
        isLoading: false,
        error: message,
      });
      throw new Error(message);
    }
  },

  verifyPhone: async (phone) => {
    set({ isLoading: true, error: null, verifyPhoneResult: null });

    try {
      const data = await authService.verifyPhone({ phone });
      set({ verifyPhoneResult: data, isLoading: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Verify phone failed";
      set({
        isLoading: false,
        error: message,
      });
      throw new Error(message);
    }
  },

  verifyOtp: async (input) => {
    set({ isLoading: true, error: null, verifyOtpResult: null });

    try {
      const data = await authService.verifyOtp(input);

      if (data.token == null) {
        set({
          phoneNotRegistered: true,
          isLoading: false,
        });
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      set({
        verifyOtpResult: { status: data.status, message: data.message },
        user: data.user,
        token: data.token,
        isLoading: false,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Verify OTP failed";
      set({
        isLoading: false,
        error: message,
      });
      throw new Error(message);
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null });

    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      set({
        user: null,
        token: null,
        error: null,
        isLoading: false,
        verifyPhoneResult: null,
        verifyOtpResult: null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Logout failed";
      set({
        isLoading: false,
        error: message,
      });
      throw new Error(message);
    }
  },

  initializeAuth: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr) as User;
        set({ user, token });
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
  },

  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    return !!token && !!userStr;
  },
}));
